import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Users, Shield, ShieldAlert, FileWarning, Banknote, Wallet, ArrowLeftRight, Landmark,
  Briefcase, LineChart, Headphones, Bell, LogOut, Settings2,
} from "lucide-react";
import { useAdminAuth } from "@/lib/adminAuth";
import BrandLogo from "@/components/common/BrandLogo";
import { useForceDarkTheme } from "@/lib/theme";

/** Sidebar follows the PaisaCX admin console; each item's `tabs` are its sub-pages. */
export const ADMIN_NAV_GROUPS = [
  {
    title: "Overview",
    items: [
      {
        label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard",
        tabs: [
          { to: "/admin/dashboard", label: "Dashboard" },
          { to: "/admin/reports", label: "Reports & analytics" },
          { to: "/admin/reports/tds", label: "TDS reports" },
        ],
      },
    ],
  },
  {
    title: "Users",
    items: [
      {
        label: "Users", icon: Users, testid: "nav-users",
        tabs: [
          { to: "/admin/users", label: "Users" },
          { to: "/admin/marketing/rewards", label: "Rewards & bonuses" },
        ],
      },
      {
        label: "KYC review", icon: Shield, testid: "nav-kyc",
        tabs: [
          { to: "/admin/kyc", label: "KYC review" },
          { to: "/admin/kyc/signzy", label: "KYC mode" },
        ],
      },
    ],
  },
  {
    title: "Money",
    items: [
      {
        label: "Funds", icon: Banknote, testid: "nav-funds",
        tabs: [
          { to: "/admin/fees", label: "Fees" },
          { to: "/admin/finance/tds", label: "TDS settings" },
          { to: "/admin/tax", label: "Taxation & GST" },
        ],
      },
      {
        label: "Wallet mgmt", icon: Wallet, testid: "nav-wallet",
        tabs: [
          { to: "/admin/wallet", label: "Wallets" },
          { to: "/admin/wallet/approvals", label: "Withdrawal approvals" },
          { to: "/admin/wallet/whitelist-approvals", label: "Withdraw whitelist" },
          { to: "/admin/wallet/deposit-whitelist-approvals", label: "Deposit whitelist" },
        ],
      },
      {
        label: "Transactions", icon: ArrowLeftRight, testid: "nav-transactions",
        tabs: [
          { to: "/admin/finance/tds/transactions", label: "TDS transactions" },
          { to: "/admin/compliance", label: "Compliance" },
        ],
      },
      {
        label: "P2P", icon: Landmark, testid: "nav-p2p",
        tabs: [{ to: "/admin/p2p/disputes", label: "P2P disputes" }],
      },
      {
        label: "OTC", icon: Briefcase, testid: "nav-otc",
        tabs: [{ to: "/admin/otc", label: "OTC desk" }],
      },
      {
        label: "Markets", icon: LineChart, testid: "nav-markets",
        tabs: [{ to: "/admin/markets", label: "Markets" }],
      },
    ],
  },
  {
    title: "Risk",
    items: [
      {
        label: "FIU / AML", icon: FileWarning, testid: "nav-aml",
        tabs: [{ to: "/admin/aml", label: "FIU / AML" }],
      },
      {
        label: "Risk management", icon: ShieldAlert, testid: "nav-risk",
        tabs: [
          { to: "/admin/risk", label: "Risk engine" },
          { to: "/admin/surveillance", label: "Surveillance" },
        ],
      },
    ],
  },
  {
    title: "Platform settings",
    items: [
      {
        label: "Sessions", icon: Shield, testid: "nav-sessions",
        tabs: [
          { to: "/admin/access-control", label: "Access control" },
          { to: "/admin/security", label: "Security controls" },
          { to: "/admin/security/sms-dlt", label: "SMS / DLT" },
        ],
      },
      {
        label: "Support", icon: Headphones, testid: "nav-support",
        tabs: [{ to: "/admin/support", label: "Support & disputes" }],
      },
      {
        label: "Notifications", icon: Bell, testid: "nav-notifications",
        tabs: [
          { to: "/admin/cms", label: "CMS & announcements" },
          { to: "/admin/blog", label: "Blog" },
          { to: "/admin/launch-gate", label: "Coming soon gate" },
        ],
      },
    ],
  },
];

const ALL_ITEMS = ADMIN_NAV_GROUPS.flatMap((g) => g.items.map((i) => ({ ...i, group: g.title })));
const ALL_TABS = ALL_ITEMS.flatMap((i) => i.tabs);

/** Longest tab path that matches the current URL (so /admin/kyc/signzy beats /admin/kyc). */
function activeTabPath(pathname) {
  let best = null;
  for (const t of ALL_TABS) {
    if (pathname === t.to || pathname.startsWith(`${t.to}/`)) {
      if (!best || t.to.length > best.length) best = t.to;
    }
  }
  return best;
}

export function Segmented({ items, value, onChange, testIdPrefix = "seg", className = "" }) {
  return (
    <div className={`flex w-fit max-w-full gap-1 overflow-x-auto rounded-lg border border-border bg-surface-2 p-1 ${className}`}>
      {items.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => onChange(t.id)}
          data-testid={`${testIdPrefix}-${t.id}`}
          className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition ${
            value === t.id ? "brand-gradient text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {t.icon && <t.icon className="h-3.5 w-3.5" />}
          {t.label}
        </button>
      ))}
    </div>
  );
}

function AdminSubTabs({ item, current }) {
  if (!item || item.tabs.length < 2) return null;
  return (
    <div className="mb-6 flex w-fit max-w-full gap-1 overflow-x-auto rounded-lg border border-border bg-surface-2 p-1" data-testid="admin-subtabs">
      {item.tabs.map((t) => (
        <Link
          key={t.to}
          to={t.to}
          className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-medium transition ${
            t.to === current ? "brand-gradient text-primary-foreground" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {t.label}
        </Link>
      ))}
    </div>
  );
}

export function AdminPageHeader({ title, description, actions }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {actions}
    </div>
  );
}

export default function AdminShell({ children, title, subtitle, actions }) {
  const { admin, logout } = useAdminAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [checked, setChecked] = useState(false);
  useForceDarkTheme();

  useEffect(() => {
    if (admin === false) {
      nav("/admin/login", { replace: true });
      return;
    }
    if (admin) setChecked(true);
  }, [admin, nav]);

  // Wait until admin session is verified — prevents child pages firing API calls without cookies
  if (admin === null || !checked || !admin) {
    return (
      <div className="grid min-h-screen place-items-center bg-background text-sm text-muted-foreground">
        Starting admin console…
      </div>
    );
  }

  const currentTab = activeTabPath(loc.pathname);
  const activeItem = ALL_ITEMS.find((i) => i.tabs.some((t) => t.to === currentTab));
  const activeTab = activeItem?.tabs.find((t) => t.to === currentTab);
  const signOut = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="flex h-screen overflow-hidden bg-background text-foreground" data-testid="admin-shell">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-border bg-surface/60 md:flex" data-testid="admin-sidebar">
        <Link to="/admin/dashboard" className="block border-b border-border px-5 py-5">
          <BrandLogo size="xs" />
          <p className="mt-2 flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-muted-foreground">
            <Settings2 className="h-3 w-3" /> Admin console
          </p>
        </Link>

        <nav className="min-h-0 flex-1 space-y-4 overflow-y-auto p-3">
          {ADMIN_NAV_GROUPS.map((group) => (
            <div key={group.title}>
              <p className="px-3 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/80">
                {group.title}
              </p>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const active = item.label === activeItem?.label;
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={item.label}
                      to={item.tabs[0].to}
                      data-testid={item.testid}
                      className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-left text-sm transition ${
                        active
                          ? "bg-primary/15 text-primary"
                          : "text-muted-foreground hover:bg-surface-2 hover:text-foreground"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </NavLink>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        <div className="shrink-0 border-t border-border p-4">
          <p className="truncate text-sm font-medium">{admin.full_name || "PaisaCX Admin"}</p>
          <p className="truncate text-xs text-muted-foreground">{admin.email}</p>
          <button
            type="button"
            onClick={signOut}
            data-testid="admin-logout"
            className="mt-3 inline-flex items-center gap-2 text-xs text-muted-foreground hover:text-down"
          >
            <LogOut className="h-3.5 w-3.5" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center justify-between gap-3 border-b border-border px-4 py-3 md:px-8" data-testid="admin-topbar">
          <p className="truncate text-sm text-muted-foreground">
            {activeItem
              ? [activeItem.group, activeItem.label, activeItem.tabs.length > 1 ? activeTab?.label : null]
                  .filter((part, i, arr) => part && part !== arr[i - 1])
                  .join(" · ")
              : "Users · Money · Platform settings"}
          </p>
          <div className="flex shrink-0 items-center gap-4">
            <Link to="/" className="text-xs text-primary hover:underline">View website</Link>
            <button type="button" onClick={signOut} className="text-xs text-muted-foreground hover:text-down md:hidden">
              Sign out
            </button>
          </div>
        </header>
        <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-border p-2 md:hidden">
          {ALL_ITEMS.map((item) => (
            <Link
              key={item.label}
              to={item.tabs[0].to}
              className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs ${item.label === activeItem?.label ? "bg-surface-2" : "text-muted-foreground"}`}
            >
              {item.label}
            </Link>
          ))}
        </div>

        <main data-scroll-root className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-6 md:px-8">
          {title && <AdminPageHeader title={title} description={subtitle} actions={actions} />}
          <AdminSubTabs item={activeItem} current={currentTab} />
          {children}
        </main>
      </div>
    </div>
  );
}

// ============== Reusable building blocks ==============
const TONE_TEXT = { up: "text-up", down: "text-down", warning: "text-warning", primary: "text-primary" };

/** Maps legacy hex `color` props onto Paisa tones. */
function toneFromColor(color) {
  const c = String(color || "").toLowerCase();
  if (["#f4455d", "#ef4444", "#f6465d"].includes(c)) return "down";
  if (["#f59e0b", "#f0b90b", "#fcd535"].includes(c)) return "warning";
  if (["#44d15b", "#10b981", "#0ecb81"].includes(c)) return "up";
  return null;
}

export function StatCard({ label, value, sub, icon: Icon, color, tone, testid }) {
  const t = tone || toneFromColor(color);
  return (
    <div className="min-w-0 rounded-xl border border-border bg-surface/50 px-4 py-3" data-testid={testid}>
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
        {Icon && <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      </div>
      <p className={`num mt-1 truncate text-2xl font-semibold ${t ? TONE_TEXT[t] : ""}`}>{value}</p>
      {sub && <p className="mt-0.5 truncate text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

const PILL_UP = /^(active|approved|completed|healthy|cleared|resolved|low|won|verified|settled|success|paid|enabled|live|online|filed|str filed)$/;
const PILL_WARN = /^(pending|pending review|pending_review|warning|medium|open|restricted|in_progress|in progress|investigating|under review|new|contacted|deposit only|deposit_only|quoted|locked|paid_marked|processing|review)$/;
const PILL_DOWN = /^(suspended|banned|rejected|failed|critical|halted|high|lost|cancelled|canceled|disputed|blocked|frozen|escalated)$/;

export function StatusPill({ value }) {
  const key = String(value || "").toLowerCase().trim();
  const tone = PILL_UP.test(key)
    ? "bg-up/[0.12] text-up"
    : PILL_WARN.test(key)
      ? "bg-warning/15 text-warning"
      : PILL_DOWN.test(key)
        ? "bg-down/[0.12] text-down"
        : "bg-surface-2 text-muted-foreground";
  return (
    <span className={`inline-block whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium capitalize ${tone}`}>
      {value ? String(value).replace(/_/g, " ") : "—"}
    </span>
  );
}

export function MockBadge({ phase = "B" }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-md bg-warning/15 px-2 py-0.5 text-[11px] font-medium text-warning"
      data-testid="mock-badge"
    >
      Sample data · Phase {phase}
    </span>
  );
}

export function Tabs({ tabs, value, onChange }) {
  return <Segmented items={tabs} value={value} onChange={onChange} testIdPrefix="tab" className="mb-5" />;
}

export function Panel({ children, className = "" }) {
  return (
    <div className={`overflow-hidden rounded-xl border border-border bg-surface/50 ${className}`}>
      {children}
    </div>
  );
}

const BTN_KIND = {
  primary: "brand-gradient text-primary-foreground",
  ghost: "border border-border bg-surface-2 text-foreground hover:bg-surface-2/70",
  danger: "bg-down text-white",
  success: "bg-up text-white",
};

export function Btn({ children, kind = "primary", className = "", ...rest }) {
  return (
    <button
      {...rest}
      className={`inline-flex h-9 items-center justify-center gap-1.5 rounded-lg px-4 text-sm font-semibold transition hover:opacity-90 disabled:opacity-50 ${BTN_KIND[kind] || BTN_KIND.primary} ${className}`}
    >
      {children}
    </button>
  );
}
